/**
 * THE CITY, AS NODES — `city -> block -> building`, the first content the hierarchy enumerates for
 * itself rather than adapting from an existing pass. See docs/hierarchical-region-system.md §3, M3c.
 *
 * WHAT MAKES THIS DIFFERENT FROM THE SCATTER. A stamp is a baked voxel model that fits inside a chunk
 * and is blitted into it. A tower is 160 m — twenty LOD-0 chunks — and can never be materialised, so
 * `emit` EVALUATES the building over `window ∩ aabb` and writes only the part this chunk owns. That
 * is the property the whole node design exists for, and it is why the building is the kind that
 * proves it.
 *
 * THREE RULES, all load-bearing:
 *
 * 1. THE BOX IS KNOWN WITHOUT EXPANDING ANYTHING. A building's height comes from its block's seed and
 *    its base from the terrain under its footprint — never from counting tiers, and later never from
 *    counting floors. `ySpan` is then free, and `generateLodColumn` can decide a column reaches 160 m
 *    before a single voxel of it exists. Get this wrong and towers come out decapitated.
 *
 * 2. THE SEAT IS SAMPLED AT FIXED WORLD POSITIONS, never on the requesting chunk's lattice. Nine
 *    points over the lot — corners, edge midpoints, centre. If the seat came from the chunk's own
 *    sampling the building would shift vertically as you walked toward it, which is the same defect
 *    `stampSeatRow` exists to prevent for trees.
 *
 * 3. NO FLOATING, BY CONSTRUCTION. `seatY` is the MAXIMUM terrain over the footprint, so the finished
 *    floor is above every part of the ground it stands on and nothing is ever buried. The solid then
 *    runs down to `min(terrain) - FOUNDATION_DEPTH`, so it reaches below the lowest ground under it
 *    and nothing can ever float. On a slope what is submerged is the plinth, which is what a plinth
 *    is. M3d's terrain pads shrink `max - min` toward zero and the plinth stops SHOWING — they are
 *    not what makes this correct.
 *
 * A plinth is also the safe direction for the clip: `generateLodColumn`'s minCy rests on "below the
 * lowest surface is solid", and a plinth is solid. A basement is the dangerous direction, which is
 * why basements wait for §4.5.
 */
import { CHUNK_SIZE, VOXEL_SCALE } from '../../../voxel/constants.js';
import { hashInt2 } from '../../../util/rng.js';
import { mat } from '../../../materials/index.js';
import { coarseWeight } from '../../../voxel/voxelData.js';
import { REACH_ANY, boxDepth2, boxDepth3, mergeSurfaceWord, minThicknessFine, slabQ, spanQ, surfaceBandFine, } from '../../../voxel/surfaceBand.js';
import { MAX_BUILDING_LOD, blockLot, blockSeed, buildingMaterialIndex, buildingTiers, blockProfileHeight, cityGrid, downtownFactor, worldFromGrid, } from '../../regions/CityLayout.js';
import { COLUMN_SIZE, DOOR_HEIGHT, DOOR_WIDTH, LOBBY_POST_WIDTH, LOBBY_STOREYS, MAX_FLOOR_LOD, MAX_ROOM_LOD, MAX_SHELL_LOD, PARAPET_HEIGHT, PARAPET_THICKNESS, PARTITION_THICKNESS, SLAB_THICKNESS, STAIR_LANDING_SLOTS, STAIR_RISER, WALL_THICKNESS, WINDOW_SILL, WINDOW_SIZE, acrossSpan, alongSpan, archOpeningDist, coreRects, corridorBand, corridorWidthFor, postPositions, roomCuts, roomRange, roomRectAt, spineAlongU, storeyCount, tierAt, tierPlate, windowCentreAlong, windowDistAlong, } from '../../regions/BuildingPlan.js';
/**
 * How far past its own geometry every node in this hierarchy declares itself.
 *
 * ONE CONSTANT FOR ALL THREE LEVELS, and that is the point: a building's box is grown by the band it
 * draws, and if the BLOCK or the CITY above it is not grown too they prune the walk before the
 * building is ever reached — a tower that vanishes for the columns beside it, which is both a hole in
 * the mesh and a column the clip stops short of. It is the band at the COARSEST level any of them
 * draws at, because a box is decided once and reused at every LOD.
 */
const CITY_BAND_FINE = surfaceBandFine(1 << MAX_BUILDING_LOD);
const CITY_SALT = 0xc17a00;
const BLOCK_NODE_SALT = 0xb10c17;
const BUILDING_SALT = 0xb1d190;
const ROOF_SALT = 0x2007f0;
const INTERIOR_SALT = 0x17e2a0;
const FLOOR_SALT_MAT = 0xf1004a;
const GROUND_SALT_MAT = 0x92040d;
/** Fine voxels the plinth reaches below the lowest ground under a footprint. */
const FOUNDATION_DEPTH = 8;
/**
 * WHAT A BUILDING IS MADE OF. One palette per surface, and the surface is what picks it — not the
 * node, not the LOD. A room's partition and the inner face of the shell beside it are the same
 * surface to someone standing in the room, so they take the same material.
 */
const BODY_MATERIALS = ['brick2', 'brick3', 'brick4', 'brick5', 'brick6', 'brick7'];
const ROOF_MATERIALS = ['roof2', 'metal2'];
/** Partitions, and the inner face of the shell where the lattice can hold two of them. */
const INTERIOR_MATERIALS = ['wood', 'wood2', 'wood3', 'concrete'];
/** What you walk on upstairs: the top of every slab. */
const FLOOR_MATERIALS = ['tile', 'tile3', 'tile4'];
/** THE GROUND FLOOR — the top row of the plinth, which is what the lobby stands on. Its own palette:
 *  street-level floors are stone and heavy tile, not the upper storeys' finish. */
const GROUND_MATERIALS = ['cobble', 'cobble2', 'brick8', 'tile3'];
/** Treads and risers. */
const STAIR_MATERIAL = mat('asphalt');
/** The underside of any horizontal slab — a floor, a terrace, the roof, a stair landing. */
const CEILING_MATERIAL = mat('plaster2');
/** The only emissive material there is (MATERIAL_EMISSION_LUT). Liquid, so it does not collide. */
const LIGHT_MATERIAL = mat('lava');
/** Height above a storey's floor a stand-in lamp sits at — the middle of its window row. */
const LAMP_HEIGHT = WINDOW_SILL + WINDOW_SIZE * 0.5;
/** A block's body material. Shared with the coarse mass, so the colour does not change at the cutoff
 *  any more than the height does. */
export function cityBodyMaterial(seed) {
    return mat(BODY_MATERIALS[buildingMaterialIndex(seed, BODY_MATERIALS.length)]);
}
function pick(seed, salt, list) {
    return list[(hashInt2(seed, salt) >>> 0) % list.length];
}
// ============== city ==============
export function makeCityKind(deps) {
    return {
        kind: 'city',
        maxLod: -1, // draws nothing of its own; above MAX_BUILDING_LOD it is not even built
        expandAtLod: MAX_BUILDING_LOD,
        emit: () => { },
        ySpan: () => null,
        children: (node) => deps.blocksOf(node.data),
    };
}
/** The city nodes overlapping a chunk request. Empty above MAX_BUILDING_LOD — the coarse rings draw
 *  the block silhouette per column instead (TerrainGenerator.cityMassTop). */
export function cityRoots(sink, deps) {
    if (sink.lod > MAX_BUILDING_LOD)
        return [];
    return deps.cities(sink.window).map((c) => cityNode(c, deps.cfg()));
}
export function cityNode(c, cfg) {
    // The cell's territory is not its lattice square (jitter + warp), so the box is the square grown by
    // that reach. Conservative on purpose: a node that prunes away ground it owns is a hole.
    const reach = cfg.cellSize * (0.5 + Math.min(0.5, cfg.jitter));
    const r = reach / VOXEL_SCALE + CITY_BAND_FINE;
    const x = c.centreX / VOXEL_SCALE, z = c.centreZ / VOXEL_SCALE;
    return {
        kind: 'city',
        id: c.id,
        seed: hashInt2(c.id, CITY_SALT),
        aabb: {
            minX: x - r, maxX: x + r,
            minY: -Infinity, maxY: Infinity, // the blocks below know their own heights; the city does not
            minZ: z - r, maxZ: z + r,
        },
        depth: 0,
        data: c,
    };
}
// ============== block ==============
export function makeBlockKind(deps) {
    return {
        kind: 'block',
        maxLod: -1,
        expandAtLod: MAX_BUILDING_LOD,
        emit: () => { },
        ySpan: () => null,
        children(node) {
            const b = deps.buildingOf(node.data);
            return b ? [b] : [];
        },
    };
}
export function blockNode(block, cfg) {
    const g = cityGrid(block.city.id, cfg.roadSpacing);
    const lot = blockLot(block.bi, block.bj, cfg.roadSpacing, cfg.roadWidth, cfg.blockInset);
    if (!lot)
        return null;
    const c = worldFromGrid((lot.u0 + lot.u1) * 0.5, (lot.v0 + lot.v1) * 0.5, block.city.centreX, block.city.centreZ, g);
    // World-axis bounds of a turned rectangle, plus the tallest a block may be. Y is left open for the
    // same reason the city's is: the building below knows its own seat, and this box only has to CONTAIN.
    const hu = (lot.u1 - lot.u0) * 0.5, hv = (lot.v1 - lot.v0) * 0.5;
    const hx = (hu * Math.abs(g.cos) + hv * Math.abs(g.sin)) / VOXEL_SCALE;
    const hz = (hu * Math.abs(g.sin) + hv * Math.abs(g.cos)) / VOXEL_SCALE;
    return {
        kind: 'block',
        id: blockSeed(block.city.id, block.bi, block.bj),
        seed: hashInt2(blockSeed(block.city.id, block.bi, block.bj), BLOCK_NODE_SALT),
        aabb: {
            minX: c.x / VOXEL_SCALE - hx - CITY_BAND_FINE, maxX: c.x / VOXEL_SCALE + hx + CITY_BAND_FINE,
            minY: -Infinity, maxY: Infinity,
            minZ: c.z / VOXEL_SCALE - hz - CITY_BAND_FINE, maxZ: c.z / VOXEL_SCALE + hz + CITY_BAND_FINE,
        },
        depth: 1,
        data: block,
    };
}
// ============== building ==============
/**
 * How finely the ground under a lot is measured, in metres, and the cap on how many taps that buys.
 *
 * NINE TAPS WAS NOT ENOUGH, and the failure is the visible one: on landform a 3x3 over a 30 m lot
 * missed a rise inside the footprint by 53 voxels — 13 m of hill standing above the finished floor,
 * i.e. a tower half-buried in a slope. A ~4 m pitch tracks the landform's detail band, and the cap
 * keeps a big lot from turning into a survey.
 */
const SEAT_TAP_PITCH = 4;
const SEAT_TAP_MAX = 9;
/**
 * ...and the ground still has to be BUILDABLE. No sampling of a cliff gives a seat that looks right:
 * a floor at the top of the drop floats over the bottom of it, and one at the bottom is buried by the
 * top. So a lot steeper than this simply has no building — which is also what a city does.
 * A SLOPE, not a fixed relief: 6 m of fall is a cliff on a small lot and a gentle grade on a big one,
 * and the old flat 24-voxel cap worked out to ~11° on a standard lot — far below what the plinth
 * (which reaches from below the lot's lowest point up to its seat) can absorb. 30° is the limit.
 */
const MAX_LOT_SLOPE = Math.tan(Math.PI / 6);
/**
 * Clearance in metres between a building and the boundary wall.
 *
 * ASKED AT EVERY TAP OVER THE LOT, not once at its centre with the lot's circumradius standing in for
 * its shape. The circumradius of a 30 m lot is 21.2 m, so a centre test excluded a 21.7 m band along
 * every boundary — and a 200 m region is five blocks across, so that was the whole outer ring of every
 * city: measured, 473 of 1150 blocks whose centre sits in their own city's territory were rejected by
 * the wall alone, 41%, with nothing else rejecting any of them. Testing the lot's real extent costs
 * nothing extra because the tap grid is already being walked for the seat.
 */
const WALL_CLEARANCE = 1;
/**
 * Survey margin on the seat, as a fraction of the lot's measured relief — the mirror of
 * FOUNDATION_DEPTH, which is the same allowance downward.
 *
 * A grid of taps cannot see between its taps, and what it misses scales with how much the ground is
 * moving: at the pitch above, the residual measured against a 7x7 reference was 53 voxels before the
 * grid was densified and 1 after. A FRACTION rather than a constant because the flat case must stay
 * exact — hi == lo means no margin at all, so a building on level ground sits ON it, with no plinth
 * showing.
 */
const SEAT_MARGIN_FRAC = 0.2;
/**
 * The building of a block, or null where nothing may stand. Everything about it — height, tiers,
 * materials, seat — is fixed here, so the AABB below is exact and the draw has nothing left to decide.
 */
export function buildingNode(block, cfg, ctx, reject, inWall) {
    const city = block.city;
    const g = cityGrid(city.id, cfg.roadSpacing);
    const lot = blockLot(block.bi, block.bj, cfg.roadSpacing, cfg.roadWidth, cfg.blockInset);
    if (!lot)
        return null;
    const hu = (lot.u1 - lot.u0) * 0.5, hv = (lot.v1 - lot.v0) * 0.5;
    const cu = (lot.u0 + lot.u1) * 0.5, cv = (lot.v0 + lot.v1) * 0.5;
    const c = worldFromGrid(cu, cv, city.centreX, city.centreZ, g);
    // Territory and coast are properties of WHERE the lot is, so they are asked once, at its centre.
    // The wall is a property of its EXTENT and is asked per tap below.
    const footprint = Math.hypot(hu, hv);
    if (reject(c.x, c.z))
        return null;
    const seed = blockSeed(city.id, block.bi, block.bj);
    const d = Math.hypot(c.x - city.centreX, c.z - city.centreZ);
    const downtown = downtownFactor(d, cfg.cellSize * 0.5, cfg.downtownFalloff);
    const storeyH = cfg.storeyHeight / VOXEL_SCALE;
    const height = blockProfileHeight(seed, cfg.buildingMinHeight, cfg.buildingMaxHeight, cfg.buildingHeightBias, downtown, storeyH);
    if (!(height > 0))
        return null;
    // THE SEAT, on a fixed world-space grid over the lot — never on the requesting chunk's lattice.
    const nu = Math.min(SEAT_TAP_MAX, Math.max(2, Math.ceil((2 * hu) / SEAT_TAP_PITCH)));
    const nv = Math.min(SEAT_TAP_MAX, Math.max(2, Math.ceil((2 * hv) / SEAT_TAP_PITCH)));
    let lo = Infinity, hi = -Infinity;
    for (let j = 0; j <= nv; j++) {
        for (let i = 0; i <= nu; i++) {
            const p = worldFromGrid(cu + (i / nu * 2 - 1) * hu, cv + (j / nv * 2 - 1) * hv, city.centreX, city.centreZ, g);
            if (inWall(p.x, p.z, WALL_CLEARANCE))
                return null;
            const h = ctx.height(p.x, p.z);
            if (h < lo)
                lo = h;
            if (h > hi)
                hi = h;
        }
    }
    if (hi - lo > (2 * Math.max(hu, hv)) / VOXEL_SCALE * MAX_LOT_SLOPE)
        return null;
    const seatY = Math.ceil(hi + (hi - lo) * SEAT_MARGIN_FRAC);
    const plinthBottom = Math.floor(lo) - FOUNDATION_DEPTH;
    // A SETBACK MAY NOT LAND INSIDE THE LOBBY. The ground floor is one double-height volume, so a
    // terrace at storey 1 would put a second footprint halfway up it.
    const tiers = buildingTiers(seed, hu, hv, height, storeyH, LOBBY_STOREYS * storeyH);
    const body = cityBodyMaterial(seed);
    const hx = (hu * Math.abs(g.cos) + hv * Math.abs(g.sin)) / VOXEL_SCALE;
    const hz = (hu * Math.abs(g.sin) + hv * Math.abs(g.cos)) / VOXEL_SCALE;
    const x = c.x / VOXEL_SCALE, z = c.z / VOXEL_SCALE;
    // THE INTERIOR PLAN. Fixed here with everything else about the building, so a floor node can be
    // built from the data alone and two chunks of the same tower cannot disagree about its plan. The
    // shafts are placed against the NARROWEST tier because they are vertical: one that fits the ground
    // floor and not the top exits through the wall at a setback.
    const narrow = tiers[tiers.length - 1];
    const narrowPlate = tierPlate(narrow.halfU, narrow.halfV);
    const alongU = narrowPlate ? spineAlongU(narrowPlate) : true;
    const cores = narrowPlate ? coreRects(narrowPlate, alongU) : [];
    const terraceStoreys = [];
    for (let i = 0; i < tiers.length - 1; i++)
        terraceStoreys.push(Math.round(tiers[i].top / storeyH));
    const data = {
        x: c.x, z: c.z, grid: g, tiers, seatY, plinthBottom, height, footprint,
        body,
        roof: mat(pick(seed, ROOF_SALT, ROOF_MATERIALS)),
        interior: mat(pick(seed, INTERIOR_SALT, INTERIOR_MATERIALS)),
        floorMat: mat(pick(seed, FLOOR_SALT_MAT, FLOOR_MATERIALS)),
        groundMat: mat(pick(seed, GROUND_SALT_MAT, GROUND_MATERIALS)),
        storeys: storeyCount(height, cfg.storeyHeight),
        storeyH,
        alongU, cores, terraceStoreys,
        lobbyStoreys: Math.min(LOBBY_STOREYS, storeyCount(height, cfg.storeyHeight)),
        windowPitch: cfg.windowPitch,
        corridorWidth: corridorWidthFor(cfg.corridorWidth),
        roomWidth: cfg.roomWidth,
    };
    // THE BAND WIDENS THE BOX (see CITY_BAND_FINE). Horizontally only: a top or a bottom face saturates
    // within one voxel of itself, so the vertical extent is unchanged by the band — but the PARAPET is
    // real geometry standing on the roof, so the top of the box moves for it.
    const bandX = CITY_BAND_FINE;
    const parapet = Math.ceil(PARAPET_HEIGHT / VOXEL_SCALE);
    return {
        kind: 'building',
        id: seed,
        seed: hashInt2(seed, BUILDING_SALT),
        // Tier 0 is the widest, so its box contains every tier above it. +1 on the max side because AABBs
        // are exclusive there and the top row is occupied.
        aabb: {
            minX: x - hx - bandX, maxX: x + hx + bandX + 1,
            minY: plinthBottom, maxY: seatY + Math.ceil(height) + parapet + 1,
            minZ: z - hz - bandX, maxZ: z + hz + bandX + 1,
        },
        depth: 2,
        data,
    };
}
/**
 * Fine-voxel Y the building reaches over one column, or -Infinity outside it.
 *
 * The tiers are ordered bottom-up and each is strictly inside the one below, so the LAST one that
 * contains the point is the highest — one scan of at most three.
 *
 * THE PARAPET IS NOT COUNTED. This is the walkable top the streamer and the marker raycast read, and
 * a 0.25 m ring round the edge is not where anything lands.
 */
export function buildingTopAt(b, worldX, worldZ) {
    const dx = worldX - b.x, dz = worldZ - b.z;
    const au = Math.abs(dx * b.grid.cos + dz * b.grid.sin);
    const av = Math.abs(-dx * b.grid.sin + dz * b.grid.cos);
    let top = -Infinity;
    for (const t of b.tiers) {
        if (au <= t.halfU && av <= t.halfV)
            top = b.seatY + t.top;
    }
    return top;
}
/**
 * How far INSIDE a window or the front door this point is, in fine voxels — negative outside one, and
 * -Infinity where the face carries none at all.
 *
 * `coord` is the position along the face and `half` its half-length, both in FINE voxels; `storey`
 * and `yIn` come from the row and are shared by both faces. Subtracted
 * from the wall slab that owns the face (`min(wall, -opening)`), which is why the value has to keep
 * going NEGATIVE outside the hole rather than stopping at a boolean: that is the reveal's own band,
 * and without it the edge of every window is a staircase even though the wall around it is smooth.
 *
 * ON A TERRACE STOREY THE WINDOW LOSES ITS SILL and becomes a door onto the balcony. Nothing else
 * changes — same row, same pitch, same reveal — so the facade still reads as a window row and the
 * floor it belongs to is simply open to the setback below it.
 *
 * ON A LOBBY STOREY THERE IS NO WALL AT ALL — it is an ARCADE. The opening is an arched bay between
 * two piers, so the ground floor reads as a building standing on arches rather than one with holes
 * punched in it. The front door goes with the wall it used to be cut into, because a building open on
 * all four sides does not have one.
 *
 * CORNERS ARE NEVER OPENINGS, and by construction rather than by a test: each opening is subtracted
 * from ONE face's slab, and at a corner the perpendicular slab is present and fills it. The post
 * lattice keeps its own corner solid on top of that, since at the lobby there is no other wall there.
 */
function openingDepth(b, coord, half, storey, yIn, terrace, lobbyY) {
    if (storey < 0)
        return -Infinity;
    const coordM = coord * VOXEL_SCALE, halfM = half * VOXEL_SCALE;
    if (lobbyY >= 0) {
        return -archOpeningDist(coordM, halfM, b.windowPitch, LOBBY_POST_WIDTH, lobbyY) / VOXEL_SCALE;
    }
    const wa = windowDistAlong(coordM, halfM, b.windowPitch, WINDOW_SIZE);
    const head = WINDOW_SILL + WINDOW_SIZE;
    const wy = terrace
        ? Math.abs(yIn - head * 0.5) - head * 0.5
        : Math.abs(yIn - (WINDOW_SILL + WINDOW_SIZE * 0.5)) - WINDOW_SIZE * 0.5;
    const d = wa > wy ? wa : wy;
    return -d / VOXEL_SCALE;
}
export function makeBuildingKind() {
    return {
        kind: 'building',
        maxLod: MAX_BUILDING_LOD,
        expandAtLod: MAX_FLOOR_LOD,
        /**
         * THE STOREYS THIS CHUNK TOUCHES, AS AN INDEX RANGE.
         *
         * A 45-storey tower crossed by an 8 m chunk yields three nodes, not forty-five for the resolver
         * to prune down to three. That difference is the whole reason the tree exists, and it is the one
         * thing about this kind that must never be relaxed into a build-then-filter.
         */
        children(node, ctx, sink) {
            if (!ctx.interiors)
                return [];
            const b = node.data;
            const lobby = Math.min(LOBBY_STOREYS, b.storeys);
            // ...minus the slab, which hangs BELOW its own floor level and so belongs to a storey whose
            // index the window's own minY no longer reaches.
            const slabT = Math.max(SLAB_THICKNESS / VOXEL_SCALE, 1);
            let lo = Math.max(0, Math.floor((sink.window.minY - slabT - b.seatY) / b.storeyH));
            // The lobby OWNS the storeys it swallowed, so a window that only reaches storey 1 must still
            // expand storey 0 — otherwise the columns and the lower flight simply go missing in that chunk.
            if (lo < lobby)
                lo = 0;
            const hi = Math.min(b.storeys - 1, Math.floor((sink.window.maxY - b.seatY) / b.storeyH));
            const out = [];
            for (let sIdx = lo; sIdx <= hi; sIdx++) {
                if (sIdx > 0 && sIdx < lobby)
                    continue; // folded into the lobby
                const f = floorNode(node, b, sIdx);
                if (f)
                    out.push(f);
            }
            return out;
        },
        ySpan(node) {
            // Free — the box was decided when the node was made, and it is the box the draw writes in.
            return { bottom: node.aabb.minY, top: node.aabb.maxY - 1 };
        },
        emit(node, sink, ctx) {
            drawBuilding(node.data, node.aabb, sink, ctx.interiors);
        },
    };
}
/**
 * Draw the part of a building this chunk owns.
 *
 * OVER `window ∩ aabb`, never over the model: a 160 m tower has no model, and the chunk holding its
 * fortieth metre must cost the same as the chunk holding its first. The loop bounds ARE that claim.
 *
 * IT IS A SIGNED FIELD, NOT A MEMBERSHIP TEST. Every surface here is measured — the distance to the
 * nearest face of whichever prism the voxel belongs to — and the band around it is written too, so
 * SurfaceNets has something to interpolate. A boolean puts every crossing at `t = 0.5` and a rotated
 * building is then a staircase the mesher has rounded off; see voxel/surfaceBand.ts.
 *
 * THE SHELL HAS TWO FACES AND THEREFORE THREE SAMPLES. SurfaceNets takes ONE material per cell, from
 * the corner of its 2x2x2 block with the highest weight (SurfaceNet.ts, `maxWeights`/`maxIdxs`), so
 * how many materials a wall can carry is decided by how many samples lie across it. At two voxels
 * they read about [0, 0.5, 0], the CORE wins every vote, and both faces end up coloured by a sample
 * belonging to neither — measured, 27.1% of consecutive cells along the inner face changed material,
 * which is a dither and not a boundary. WALL_THICKNESS is three voxels for exactly this reason: a
 * face cell then holds that face's own boundary sample and air, and the core is out of its reach.
 * The split is still gated on the wall really being three samples thick, because the L1 floor makes
 * it 1.41 samples at every coarse level however many metres it claims.
 *
 * THE SOLID IS A UNION OF BOXES IN THE BUILDING'S OWN FRAME, which is what makes that tractable:
 *
 *   plinth      tier 0's footprint, [plinthBottom, seatY]
 *   tier m      tier m's footprint, [plinthBottom, seatY + top_m]   — solid form
 *   wall u/v    tier m's footprint inset by wallT on ONE axis       — hollow form
 *   cap         tier m's footprint, its top row                     — hollow form, the terrace
 *   parapet     tier m's perimeter, standing on the cap             — hollow form
 *
 * Union is a max of depths and subtraction is `min(depth, -other)`. Tiers are nested, so extending
 * every tier's prism down to the plinth costs nothing and removes the stacking bookkeeping. The two
 * wall slabs are written in |u| and |v|, so ONE evaluation covers both faces of an axis and their
 * union keeps the corners solid, which is also why a window can never notch one.
 *
 * HOLLOW IS STILL ADDITIVE. Nothing carves the world: the void between the slabs is simply a place no
 * prism reaches, so the no-carve rule that protects the terrain and the region wall is untouched and
 * the floors and rooms below write into air that was never filled. Above MAX_SHELL_LOD there are no
 * slabs at all — a 0.5 m wall cannot exist in a 2 m voxel — and the tower is its solid massing.
 */
function drawBuilding(b, aabb, sink, interiors) {
    const scale = 1 << sink.lod;
    const span = CHUNK_SIZE * scale; // the chunk's footprint in fine voxels
    const x0 = sink.cx * span, y0 = sink.cy * span, z0 = sink.cz * span;
    const band = surfaceBandFine(scale);
    // The intersection, in this chunk's own sample indices. Empty loops where the box misses.
    //
    // THE DECLARED BOX IS WIDER THAN THIS ONE, deliberately: it carries the band at the COARSEST level
    // the node draws at, because it is decided once and reused at every LOD. Walking it at lod 0 would
    // be 8 m of empty columns on every side, so the loop takes the box THIS level needs and the
    // declared one stays what it must be — a superset, which is all the containment rule asks.
    const base0 = b.tiers[0];
    const hx = (base0.halfU * Math.abs(b.grid.cos) + base0.halfV * Math.abs(b.grid.sin)) / VOXEL_SCALE;
    const hz = (base0.halfU * Math.abs(b.grid.sin) + base0.halfV * Math.abs(b.grid.cos)) / VOXEL_SCALE;
    const bx = b.x / VOXEL_SCALE, bz = b.z / VOXEL_SCALE;
    const iLo = Math.max(0, Math.ceil((bx - hx - band - x0) / scale));
    const iHi = Math.min(CHUNK_SIZE - 1, Math.floor((bx + hx + band - x0) / scale));
    const kLo = Math.max(0, Math.ceil((bz - hz - band - z0) / scale));
    const kHi = Math.min(CHUNK_SIZE - 1, Math.floor((bz + hz + band - z0) / scale));
    const jLo = Math.max(0, Math.ceil((aabb.minY - y0) / scale));
    const jHi = Math.min(CHUNK_SIZE - 1, Math.floor((aabb.maxY - 1 - y0) / scale));
    if (iLo > iHi || kLo > kHi || jLo > jHi)
        return;
    const nT = b.tiers.length;
    const hollow = interiors && sink.lod <= MAX_SHELL_LOD;
    // FLOORED AT WHAT THE LATTICE CAN HOLD, which for a wall turned to an arbitrary yaw is the L1 norm
    // of its own normal — `reach`, the same number the road downsample already needed. One coarse voxel
    // is the AXIS-ALIGNED bound and is up to 41% short of it: measured, a lod-1 wall then rendered
    // 0.27 m thick where it claimed 0.5, and dropped out entirely at 3 places in 832.
    const reach = Math.abs(b.grid.cos) + Math.abs(b.grid.sin);
    const minT = minThicknessFine(reach, scale);
    const wallT = Math.max(WALL_THICKNESS / VOXEL_SCALE, minT);
    // Two faces need three samples between them. Below that the wall carries one material, and it is
    // the one you see from the street.
    const splitFaces = wallT >= 3 * scale;
    const fenceT = Math.max(PARAPET_THICKNESS / VOXEL_SCALE, minT);
    const fenceH = PARAPET_HEIGHT / VOXEL_SCALE;
    const plateInset = WALL_THICKNESS / VOXEL_SCALE;
    // No shaft means no floor nodes, so nothing will lay a floor over this cap and it stays a roof.
    const hasFloors = interiors && b.cores.length > 0;
    // A CAP IS A SLAB, NOT A MEMBRANE. At one voxel it could carry only one material, so the top
    // storey's ceiling came out in roofing and so did the underside of every terrace ring — the one
    // place a cap is genuinely the ceiling of the room below it, because the floor slabs are drawn to
    // the PLATE and stop short of the ring. Given the floor slab's own thickness it holds two rows and
    // reads the same way round as every other floor in the building.
    const capT = Math.max(SLAB_THICKNESS / VOXEL_SCALE, scale);
    /**
     * THE INTERIOR'S LAMPS, AS A COARSE STAND-IN — the same trade stamps make between a folded model
     * and `lampPart`. Below MAX_FLOOR_LOD the real lights are drawn by the floors and rooms that own
     * them; above it there are no floor nodes at all, so the shell puts one lamp per storey at every
     * WINDOW, which is where a light has to be for any of it to reach the street. One voxel of this
     * level, snapped to it, standing in AIR just inside the glass: `lava` is a liquid-type material,
     * so a lamp inside the wall would take that voxel out of the solid mesh layer and hole it.
     *
     * It stops with the hollow shell. Above MAX_SHELL_LOD a tower is solid massing with no openings,
     * and a light behind three metres of brick is a light nobody sees.
     */
    const standIn = hollow && sink.lod > MAX_FLOOR_LOD;
    const lampInset = wallT + scale * 0.5;
    const hv = scale * 0.5;
    const lastTop = b.seatY + b.tiers[nT - 1].top;
    // Per-tier geometry in FINE voxels, hoisted out of the column loop.
    //
    // A TIER'S WALLS START WHERE THE ONE BELOW ENDS. Its solid PRISM may run all the way down to the
    // plinth — tiers are nested, so a narrower box inside a wider one adds nothing — but its wall
    // slabs may not: a narrower ring drawn from the seat upward is a second wall standing inside the
    // storeys below it. Measured when this was got wrong: 26% more solid voxels at lod 1, an interior
    // partition ring through every lower floor of every setback tower.
    for (let m = 0; m < nT; m++) {
        tierHU[m] = b.tiers[m].halfU / VOXEL_SCALE;
        tierHV[m] = b.tiers[m].halfV / VOXEL_SCALE;
        tierTop[m] = b.seatY + b.tiers[m].top;
        tierBot[m] = m === 0 ? b.seatY : b.seatY + b.tiers[m - 1].top;
    }
    // THE ROW CONSTANTS, PRECOMPUTED. Storey, height within it and whether that storey sits on a
    // terrace depend on the ROW alone, so computing them per column was one divide and a search per
    // voxel of every wall in the city.
    if (hollow) {
        for (let j = jLo; j <= jHi; j++) {
            const fineY = y0 + j * scale;
            const above = fineY - b.seatY;
            const s = above >= 0 ? Math.floor(above / b.storeyH) : -1;
            rowStorey[j] = s;
            rowYIn[j] = (above - s * b.storeyH) * VOXEL_SCALE;
            let t = 0;
            for (let q = 0; q < b.terraceStoreys.length; q++)
                if (b.terraceStoreys[q] === s) {
                    t = 1;
                    break;
                }
            rowTerrace[j] = t;
            // Height above the LOBBY's own floor, or -1 where this row is not in the lobby at all. The
            // arch spans two storeys, so a height measured within one of them is the wrong quantity.
            rowLobby[j] = s >= 0 && s < b.lobbyStoreys ? (fineY - b.seatY) * VOXEL_SCALE : -1;
            // The one row per storey a stand-in lamp sits on, snapped to this level's lattice.
            rowLamp[j] = s >= 0
                && snapTo(b.seatY + s * b.storeyH + LAMP_HEIGHT / VOXEL_SCALE, scale) === fineY ? 1 : 0;
        }
    }
    // The stair shafts, which punch every TERRACE cap so the flights are not sealed at a setback, and
    // the ROOF over the half the last flight arrives on — a hatch rather than an open shaft.
    const cores = b.cores;
    const nC = cores.length;
    // ...and the punch reaches ABOVE the roof, because a span that ENDS on the cap's top row leaves
    // that row at depth exactly 0 — which is solid, and is a lid on the stairwell one voxel thick.
    const shaftTop = lastTop + fenceH + scale;
    const coreYc = (b.seatY + shaftTop) * 0.5, coreYh = (shaftTop - b.seatY) * 0.5;
    const lastUp = (b.storeys - 1) % 2 === 0;
    for (let k = kLo; k <= kHi; k++) {
        const worldZ = (z0 + k * scale) * VOXEL_SCALE;
        for (let i = iLo; i <= iHi; i++) {
            const worldX = (x0 + i * scale) * VOXEL_SCALE;
            // Offsets from the building's own centre in its grid's axes — the phase drops out, so this is
            // the rotation alone.
            const dx = worldX - b.x, dz = worldZ - b.z;
            const su = (dx * b.grid.cos + dz * b.grid.sin) / VOXEL_SCALE;
            const sv = (-dx * b.grid.sin + dz * b.grid.cos) / VOXEL_SCALE;
            const au = su < 0 ? -su : su, av = sv < 0 ? -sv : sv;
            // Broad phase on the widest tier, band included (perf-guide invariant 3).
            const qu0 = au - tierHU[0], qv0 = av - tierHV[0];
            if (qu0 > band || qv0 > band)
                continue;
            // WHICH PRISMS THIS COLUMN CAN REACH AT ALL — usually one, never more than a few. Everything
            // below is then a short list rather than a scan of every tier at every row.
            let nActive = 0;
            for (let m = 0; m < nT; m++) {
                const qu = au - tierHU[m], qv = av - tierHV[m];
                if (qu > band || qv > band)
                    continue;
                actTier[nActive] = m;
                actQU[nActive] = qu;
                actQV[nActive] = qv;
                // The wall slabs, in |u| and |v|: one evaluation per axis covers both of its faces.
                actWU[nActive] = slabQ(au, tierHU[m] - wallT * 0.5, wallT * 0.5);
                actWV[nActive] = slabQ(av, tierHV[m] - wallT * 0.5, wallT * 0.5);
                // ...and WHICH of the two slabs it can reach. An interior column reaches neither, an ordinary
                // wall column one, a corner both — so the openings, which are the dear part of the row loop,
                // are asked only where there is a face for them to be in.
                actNear[nActive] = (actWU[nActive] <= band ? 1 : 0) | (actWV[nActive] <= band ? 2 : 0);
                // Which half of the slab this sample is in — the inner one takes the interior material, so
                // the shell beside you in a room and the partition in front of you read as one surface.
                actInner[nActive] = (au < tierHU[m] - wallT * 0.5 ? 1 : 0)
                    | (av < tierHV[m] - wallT * 0.5 ? 2 : 0);
                // The parapet, on the tier's own perimeter, standing on its cap.
                actFU[nActive] = slabQ(au, tierHU[m] - fenceT * 0.5, fenceT * 0.5);
                actFV[nActive] = slabQ(av, tierHV[m] - fenceT * 0.5, fenceT * 0.5);
                // IS THERE A FLOOR OVER THIS CAP? Not "inside THIS tier's plate" — that is nearly the whole
                // footprint and made the exposed terrace ring come out in floor tile. The storey seated on a
                // terrace belongs to the tier ABOVE and lays its slab over THAT plate, so everything outside
                // it is open sky and takes the roof's material.
                actIn[nActive] = m + 1 < nT
                    && au < tierHU[m + 1] - plateInset && av < tierHV[m + 1] - plateInset ? 1 : 0;
                nActive++;
            }
            if (nActive === 0)
                continue;
            for (let c = 0; c < nC; c++) {
                coreQU[c] = spanQ(su, cores[c].u0 / VOXEL_SCALE, cores[c].u1 / VOXEL_SCALE);
                coreQV[c] = spanQ(sv, cores[c].v0 / VOXEL_SCALE, cores[c].v1 / VOXEL_SCALE);
                // ...and the roof HATCH is that shaft narrowed to the half the last flight arrives on. An
                // open 4x8 hole at the top of every tower is a fall down the whole stairwell.
                const acr = acrossSpan(cores[c], b.alongU);
                const mid = (acr.lo + acr.hi) * 0.5 / VOXEL_SCALE;
                const l0 = lastUp ? acr.lo / VOXEL_SCALE : mid, l1 = lastUp ? mid : acr.hi / VOXEL_SCALE;
                const half = spanQ(b.alongU ? sv : su, l0, l1);
                coreHU[c] = b.alongU ? coreQU[c] : half;
                coreHV[c] = b.alongU ? half : coreQV[c];
            }
            for (let j = jLo; j <= jHi; j++) {
                const fineY = y0 + j * scale;
                // The plinth, always solid — its top IS the ground floor, so ITS TOP ROW IS THE GROUND
                // FLOOR'S MATERIAL (groundMat), the way a slab's top row is floorMat. Hollow form only: the
                // solid form's plinth top is interior to tier 0 and covered anyway, except for a one-row ring
                // at the base of the outside wall, which should stay the wall's own material.
                let depth = boxDepth3(qu0, qv0, spanQ(fineY, b.plinthBottom, b.seatY));
                let material = hollow && fineY > b.seatY - scale ? b.groundMat : b.body;
                if (!hollow) {
                    // The last row of each tier is its roof, so setbacks read as terraces rather than as a
                    // colour change halfway up a wall.
                    let capRow = false;
                    for (let a = 0; a < nActive; a++) {
                        const t = tierTop[actTier[a]];
                        if (fineY > t - capT && fineY <= t) {
                            capRow = true;
                            break;
                        }
                    }
                    for (let a = 0; a < nActive; a++) {
                        const d = boxDepth3(actQU[a], actQV[a], spanQ(fineY, b.plinthBottom, tierTop[actTier[a]]));
                        if (d > depth) {
                            depth = d;
                            material = capRow ? b.roof : b.body;
                        }
                    }
                }
                else {
                    let wallD = -Infinity, wallMat = b.body;
                    let capD = -Infinity, capMat = b.roof;
                    let roofD = -Infinity, roofMat = b.roof, fenceD = -Infinity;
                    const storey = rowStorey[j];
                    const yIn = rowYIn[j];
                    const terrace = rowTerrace[j] === 1;
                    const lobbyY = rowLobby[j];
                    for (let a = 0; a < nActive; a++) {
                        const m = actTier[a];
                        const near = actNear[a];
                        const indoors = actIn[a] === 1 && hasFloors;
                        /**
                         * THE BOTTOM ROW OF A TIER'S WALL IS THE FLOOR IT STANDS ON. At a setback the wall's
                         * span STARTS at the terrace, so its bottom face and the floor's top face are the same
                         * row — both at depth exactly 0, and max-merge replaces on a strict increase, so the
                         * wall (drawn before any floor node) took it. Measured across a terrace doorway, the
                         * threshold you step over read `wood2 wood2 brick brick` instead of the floor's tile.
                         * The mirror of the ceiling row on a slab, and stated the same way.
                         */
                        const wallMaterial = (axis) => m > 0 && fineY < tierBot[m] + scale ? b.floorMat
                            // A PIER IN THE ARCADE HAS NO INSIDE. The inner/outer split says which face of a wall
                            // you are looking at, and the lobby has no wall — a free-standing pier is seen from
                            // every side at once, and all of them are the outside of the building.
                            : splitFaces && lobbyY < 0 && (actInner[a] & axis) ? b.interior : b.body;
                        if (near !== 0) {
                            const qyT = spanQ(fineY, tierBot[m], tierTop[m]);
                            // Openings carve the face they belong to and only that face, so each slab subtracts its
                            // own. At a corner both slabs are present and each keeps the other's opening filled.
                            if (near & 1) {
                                let wu = boxDepth3(actWU[a], actQV[a], qyT);
                                const o = openingDepth(b, sv, tierHV[m], storey, yIn, terrace, lobbyY);
                                if (-o < wu)
                                    wu = -o;
                                if (wu > wallD) {
                                    wallD = wu;
                                    wallMat = wallMaterial(1);
                                }
                            }
                            if (near & 2) {
                                let wv = boxDepth3(actQU[a], actWV[a], qyT);
                                const o = openingDepth(b, su, tierHU[m], storey, yIn, terrace, lobbyY);
                                if (-o < wv)
                                    wv = -o;
                                if (wv > wallD) {
                                    wallD = wv;
                                    wallMat = wallMaterial(2);
                                }
                            }
                        }
                        // The cap is one row at the tier's top, and the parapet stands on it; skip the whole
                        // column of rows below them.
                        if (fineY < tierTop[m] - capT - band || fineY > tierTop[m] + fenceH + band)
                            continue;
                        const qyC = spanQ(fineY, tierTop[m] - capT, tierTop[m]);
                        const c = boxDepth3(actQU[a], actQV[a], qyC);
                        if (tierTop[m] === lastTop) {
                            if (c > roofD) {
                                roofD = c;
                                roofMat = fineY < lastTop - capT + scale ? CEILING_MATERIAL : b.roof;
                            }
                        }
                        else if (c > capD) {
                            capD = c;
                            // A TERRACE CAP IS A ROOF OUTSIDE, A FLOOR INSIDE, AND A CEILING UNDERNEATH — and it
                            // has to say all three, because it covers the tier's WHOLE footprint while the storey
                            // seated on it puts its slab only over the plate. At the top row the two are boundary
                            // samples at depth exactly 0 and max-merge replaces on a STRICT increase, so the cap,
                            // drawn first, keeps the row; and out on the ring there is no slab at all, so its
                            // bottom row is the only ceiling the room below it has.
                            capMat = fineY < tierTop[m] - capT + scale ? CEILING_MATERIAL
                                : (indoors ? b.floorMat : b.roof);
                        }
                        // A SMALL FENCE ROUND EVERY CAP — the terraces you can now walk out onto, and the roof
                        // the stairs now reach. Same slab form as the wall, in |u| and |v|, so the corners close.
                        const qyF = spanQ(fineY, tierTop[m], tierTop[m] + fenceH);
                        if (qyF <= band) {
                            const fu = boxDepth3(actFU[a], actQV[a], qyF);
                            const fv = boxDepth3(actQU[a], actFV[a], qyF);
                            const fd = fu > fv ? fu : fv;
                            if (fd > fenceD)
                                fenceD = fd;
                        }
                    }
                    // The shafts punch the terraces whole, and the roof over the arriving flight only.
                    if (nC > 0) {
                        const qyS = slabQ(fineY, coreYc, coreYh);
                        if (capD > -Infinity) {
                            for (let c = 0; c < nC; c++) {
                                const cd = boxDepth3(coreQU[c], coreQV[c], qyS);
                                if (-cd < capD)
                                    capD = -cd;
                            }
                        }
                        if (roofD > -Infinity) {
                            for (let c = 0; c < nC; c++) {
                                const cd = boxDepth3(coreHU[c], coreHV[c], qyS);
                                if (-cd < roofD)
                                    roofD = -cd;
                            }
                        }
                    }
                    if (standIn && rowLamp[j] === 1) {
                        const a0 = actTier[0];
                        const onU = actWU[0] <= actWV[0];
                        const half = (onU ? tierHV[a0] : tierHU[a0]) * VOXEL_SCALE;
                        const c = windowCentreAlong((onU ? sv : su) * VOXEL_SCALE, half, b.windowPitch, WINDOW_SIZE);
                        if (c < Infinity) {
                            const inset = (onU ? tierHU[a0] : tierHV[a0]) - lampInset;
                            const cf = c / VOXEL_SCALE;
                            const lu = onU ? (su < 0 ? -inset : inset) : cf;
                            const lv = onU ? cf : (sv < 0 ? -inset : inset);
                            const lx = snapTo(bx + lu * b.grid.cos - lv * b.grid.sin, scale);
                            const lz = snapTo(bz + lu * b.grid.sin + lv * b.grid.cos, scale);
                            const d = boxDepth3(Math.abs(x0 + i * scale - lx) - hv, Math.abs(z0 + k * scale - lz) - hv, -hv);
                            if (d > depth) {
                                depth = d;
                                material = LIGHT_MATERIAL;
                            }
                        }
                    }
                    if (wallD > depth) {
                        depth = wallD;
                        material = wallMat;
                    }
                    if (capD > depth) {
                        depth = capD;
                        material = capMat;
                    }
                    if (roofD > depth) {
                        depth = roofD;
                        material = roofMat;
                    }
                    if (fenceD > depth) {
                        depth = fenceD;
                        material = b.body;
                    }
                }
                const w = coarseWeight(depth, scale);
                if (w <= -0.5)
                    continue;
                const index = i + j * CHUNK_SIZE + k * CHUNK_SIZE * CHUNK_SIZE;
                // Max-merge: terrain and the region wall are written before the walk and a building is
                // additive, so nothing here can lower what is already there.
                sink.data[index] = mergeSurfaceWord(sink.data[index], w, material);
            }
        }
    }
}
/** Per-column scratch for the active-prism list. Bounded by CityLayout's MAX_TIERS. */
const MAX_ACTIVE_TIERS = 4;
const MAX_CORES = 2;
const tierHU = new Float64Array(MAX_ACTIVE_TIERS);
const tierHV = new Float64Array(MAX_ACTIVE_TIERS);
const tierTop = new Float64Array(MAX_ACTIVE_TIERS);
const tierBot = new Float64Array(MAX_ACTIVE_TIERS);
const actTier = new Int32Array(MAX_ACTIVE_TIERS);
const actQU = new Float64Array(MAX_ACTIVE_TIERS);
const actQV = new Float64Array(MAX_ACTIVE_TIERS);
const actWU = new Float64Array(MAX_ACTIVE_TIERS);
const actWV = new Float64Array(MAX_ACTIVE_TIERS);
const actFU = new Float64Array(MAX_ACTIVE_TIERS);
const actFV = new Float64Array(MAX_ACTIVE_TIERS);
const actNear = new Int32Array(MAX_ACTIVE_TIERS);
const actInner = new Int32Array(MAX_ACTIVE_TIERS);
const actIn = new Int32Array(MAX_ACTIVE_TIERS);
const coreQU = new Float64Array(MAX_CORES);
const coreQV = new Float64Array(MAX_CORES);
const coreHU = new Float64Array(MAX_CORES);
const coreHV = new Float64Array(MAX_CORES);
/** Per-ROW scratch: storey, height within it, and whether that storey's floor is a terrace. */
const rowStorey = new Int32Array(CHUNK_SIZE);
const rowYIn = new Float64Array(CHUNK_SIZE);
const rowTerrace = new Uint8Array(CHUNK_SIZE);
const rowLobby = new Float64Array(CHUNK_SIZE);
const rowLamp = new Uint8Array(CHUNK_SIZE);
// ============== floor ==============
const FLOOR_SALT = 0xf1002;
const FLOOR_BAND = surfaceBandFine(1 << MAX_FLOOR_LOD);
const ROOM_BAND = surfaceBandFine(1 << MAX_ROOM_LOD);
const ROOM_NODE_SALT = 0x2001;
/** True for the ground floor: one double-height open volume, columns and stairs and nothing else. */
export function isLobby(f) {
    return f.storey === 0;
}
/**
 * One storey. Null where the plan does not fit — a tower too slim for a shaft has no interior, and
 * says so once here rather than in three drawing functions.
 */
export function floorNode(parent, b, storey) {
    if (b.cores.length === 0)
        return null;
    const lobby = Math.min(LOBBY_STOREYS, b.storeys);
    const span = storey === 0 ? lobby : 1;
    const slabY = b.seatY + Math.round(storey * b.storeyH);
    // THE TIER AT THE TOP OF THE VOLUME, not at its slab. Setbacks are snapped to floor levels, so a
    // storey never straddles two footprints — and taking the top is what makes a storey seated ON a
    // terrace use the NARROWER tier above it. Taking the slab's tier instead is what left room walls
    // standing out over the balcony.
    const t = tierAt(b.tiers, slabY + span * b.storeyH - b.seatY - 1);
    if (!t)
        return null;
    const plate = tierPlate(t.halfU, t.halfV);
    if (!plate)
        return null;
    const band = corridorBand(plate, b.alongU, b.corridorWidth);
    // Rooms are the strips OUTSIDE the corridor band, so a plate no wider than the band holds none.
    const acr = acrossSpan(plate, b.alongU), bnd = acrossSpan(band, b.alongU);
    const depth = Math.min(bnd.lo - acr.lo, acr.hi - bnd.hi);
    // The cuts come from THIS tier's window lattice, so a partition can never arrive mid-window. They
    // run the WHOLE plate: the corridor's clearance is what keeps a wall off a flight, so there is no
    // longer any reason to banish the rooms to the middle. The lobby has no partitions but still wants
    // the lines — its colonnade stands on them.
    const len = alongSpan(plate, b.alongU);
    const cuts = roomCuts(len.lo, len.hi, b.alongU ? t.halfU : t.halfV, b.windowPitch, WINDOW_SIZE, PARTITION_THICKNESS, b.roomWidth, hashInt2(parent.seed, storey));
    const rooms = storey === 0 || depth < MIN_ROOM_DEPTH ? 0 : Math.max(0, cuts.length - 1);
    const hx = (t.halfU * Math.abs(b.grid.cos) + t.halfV * Math.abs(b.grid.sin)) / VOXEL_SCALE;
    const hz = (t.halfU * Math.abs(b.grid.sin) + t.halfV * Math.abs(b.grid.cos)) / VOXEL_SCALE;
    const x = b.x / VOXEL_SCALE, z = b.z / VOXEL_SCALE;
    const data = { building: b, storey, span, slabY, tier: t, plate, band, cuts, rooms };
    return {
        kind: 'floor',
        id: hashInt2(parent.id, storey),
        seed: hashInt2(hashInt2(parent.id, storey), FLOOR_SALT),
        // INSIDE the building's box by construction — interiors can never extend a tower, which is why
        // nothing in M5 reaches the vertical clip. Widened by the band at the coarsest level a floor
        // draws at, as the building's box is.
        aabb: {
            minX: x - hx - FLOOR_BAND, maxX: x + hx + FLOOR_BAND + 1,
            // THE SLAB HANGS BELOW ITS FLOOR LEVEL, so the box starts under it. See drawFloor.
            minY: slabY - Math.ceil(SLAB_THICKNESS / VOXEL_SCALE) - 1,
            maxY: slabY + Math.ceil(span * b.storeyH) + 1,
            minZ: z - hz - FLOOR_BAND, maxZ: z + hz + FLOOR_BAND + 1,
        },
        depth: 3,
        data,
    };
}
/** A strip narrower than this beside the corridor is left as open floor rather than sliced. */
const MIN_ROOM_DEPTH = 2.5;
export function makeFloorKind() {
    return {
        kind: 'floor',
        maxLod: MAX_FLOOR_LOD,
        expandAtLod: MAX_ROOM_LOD,
        ySpan: (node) => ({ bottom: node.aabb.minY, top: node.aabb.maxY - 1 }),
        emit(node, sink) {
            drawFloor(node.data, node.aabb, sink);
        },
        /**
         * The rooms this chunk touches, again as an index range rather than a filtered list — two or
         * three of a plate's fourteen.
         */
        children(node, _ctx, sink) {
            if (sink.lod > MAX_ROOM_LOD)
                return [];
            const f = node.data;
            if (f.rooms <= 0)
                return [];
            // The chunk's window, brought into the building's own axes. A rotated box has no exact image
            // under rotation, so this is its bounding interval — conservative, which is what pruning wants.
            const { lo, hi } = windowSpanInGrid(f, sink);
            const { kLo, kHi } = roomRange(f.cuts, lo, hi);
            const out = [];
            for (let k = kLo; k <= kHi; k++) {
                for (const side of [-1, 1]) {
                    const rect = roomRectAt(f.plate, f.band, f.building.alongU, f.cuts, k, side);
                    if (rect.u1 <= rect.u0 || rect.v1 <= rect.v0)
                        continue;
                    out.push(roomNode(node, f, rect, k, side));
                }
            }
            return out;
        },
    };
}
/** The chunk window's extent along the floor's spine axis, in the building's own metres. */
function windowSpanInGrid(f, sink) {
    const b = f.building;
    const w = sink.window;
    const cxm = (w.minX + w.maxX) * 0.5 * VOXEL_SCALE - b.x;
    const czm = (w.minZ + w.maxZ) * 0.5 * VOXEL_SCALE - b.z;
    const hxm = (w.maxX - w.minX) * 0.5 * VOXEL_SCALE;
    const hzm = (w.maxZ - w.minZ) * 0.5 * VOXEL_SCALE;
    const c = Math.abs(b.grid.cos), sn = Math.abs(b.grid.sin);
    // A world-axis box has no exact image under rotation, so this is its bounding interval on the
    // grid axis — conservative, which is what pruning wants.
    const centre = b.alongU
        ? cxm * b.grid.cos + czm * b.grid.sin
        : -cxm * b.grid.sin + czm * b.grid.cos;
    const reach = b.alongU ? hxm * c + hzm * sn : hxm * sn + hzm * c;
    return { lo: centre - reach, hi: centre + reach };
}
/** Fine-voxel row range a chunk's Y window and a box share, in this chunk's sample indices. */
function rowRange(aabb, scale, y0) {
    return {
        jLo: Math.max(0, Math.ceil((aabb.minY - y0) / scale)),
        jHi: Math.min(CHUNK_SIZE - 1, Math.floor((aabb.maxY - 1 - y0) / scale)),
    };
}
/** Onto this level's own lattice — a lamp centred off it catches two rows either side and draws four
 *  voxels of light where one was meant (stamps/coarseModel.ts, lampPart). */
function snapTo(v, scale) {
    return Math.round(v / scale) * scale;
}
/** Index of the nearest entry. Called once per COLUMN over a list of at most a handful. */
function nearest(arr, x) {
    let best = 0, bd = Infinity;
    for (let i = 0; i < arr.length; i++) {
        const d = arr[i] > x ? arr[i] - x : x - arr[i];
        if (d < bd) {
            bd = d;
            best = i;
        }
    }
    return best;
}
/**
 * The slab under a storey, the flights that climb out of it, and — on the ground floor — the columns
 * that hold up a lobby with no walls in it.
 *
 * THE SLAB HANGS BELOW ITS FLOOR LEVEL — `[slabY - slabT, slabY]`, not `[slabY, slabY + slabT]`.
 * That makes `slabY` the FINISHED FLOOR at every storey, which is the only way the interior can be
 * flush with a terrace: a cap's deck tops out at the tier's own top, and the storey standing on it
 * has its slabY there too. Hanging it above instead put every balcony half a metre below the room it
 * opens onto. Storey 0 was already right — its floor IS the plinth top — and the clear height is
 * unchanged either way, at `storeyH - slabT`.
 *
 * STOREY 0 HAS NO SLAB: its floor is the top of the plinth, which the shell has already drawn solid
 * across the whole base tier.
 *
 * The slab is OPEN OVER THE ARRIVING HALF OF EVERY SHAFT — that opening is what the stairs climb
 * through, and leaving it out is the one mistake that would make every floor above the first
 * unreachable while still looking perfectly correct from outside.
 *
 * THE LOBBY IS TWO STOREYS AND DRAWS TWO FLIGHTS. The switchback's half alternates with the storey
 * index, so the lobby's own two flights use both halves and the storey above lands back on the first
 * — the same rule as everywhere else, just asked twice in one node.
 *
 * A FLIGHT IS ITS DIAGONAL, NOT A WEDGE. Each tread is a slab one riser deep, so consecutive treads
 * meet exactly and the underside follows the stair instead of being filled solid to the floor below.
 * The FOOT slot is the exception and is the landing: it takes the slab's own thickness and spans the
 * WHOLE shaft rather than its half, which is what bridges the two flights of a switchback — without
 * it the turn is a single half-metre tread with a hole beside it.
 *
 * AND THE TOP STOREY HAS A FLIGHT TOO, climbing to the roof. The heights make that exact: the tower's
 * height is a whole number of storeys, so the last flight's top IS the last tier's top.
 */
function drawFloor(f, aabb, sink) {
    const b = f.building;
    const scale = 1 << sink.lod;
    const span = CHUNK_SIZE * scale;
    const x0 = sink.cx * span, y0 = sink.cy * span, z0 = sink.cz * span;
    const iLo = Math.max(0, Math.ceil((aabb.minX - x0) / scale));
    const iHi = Math.min(CHUNK_SIZE - 1, Math.floor((aabb.maxX - 1 - x0) / scale));
    const kLo = Math.max(0, Math.ceil((aabb.minZ - z0) / scale));
    const kHi = Math.min(CHUNK_SIZE - 1, Math.floor((aabb.maxZ - 1 - z0) / scale));
    const { jLo, jHi } = rowRange(aabb, scale, y0);
    if (iLo > iHi || kLo > kHi || jLo > jHi)
        return;
    const band = surfaceBandFine(scale);
    const iv = 1 / VOXEL_SCALE;
    const pU0 = f.plate.u0 * iv, pU1 = f.plate.u1 * iv;
    const pV0 = f.plate.v0 * iv, pV1 = f.plate.v1 * iv;
    const slabT = Math.max(SLAB_THICKNESS / VOXEL_SCALE, scale);
    const slab = f.storey > 0;
    const slabBottom = f.slabY - slabT;
    // The underside of the slab above — what a ceiling light hangs from.
    const ceilY = f.slabY + f.span * b.storeyH - slabT;
    // Stairs and columns are one-fine-voxel detail: there is no coarser form of them. LIGHTS are not
    // detail — they are the reason you can see anything — so they follow the stamps' lamp rule instead
    // and are drawn at every level this node is, as ONE COARSE VOXEL apiece (stamps/coarseModel.ts:
    // block light seeds only from a voxel that packs SOLID, so the smallest possible light is one full
    // voxel of whatever level is being drawn).
    const detail = sink.lod <= MAX_ROOM_LOD;
    const lights = sink.lod <= MAX_FLOOR_LOD;
    const lobby = isLobby(f);
    const riserF = STAIR_RISER / VOXEL_SCALE;
    // A step is now ONE fine voxel tall, which is the lattice's own floor — and a surface sitting on
    // that floor reconstructs at 84% of it (voxel/surfaceBand.ts). The tread's SLAB is therefore
    // floored independently of the rise it expresses: consecutive treads simply overlap.
    const treadT = Math.max(riserF, minThicknessFine(REACH_ANY, scale));
    const risers = Math.max(1, Math.round(b.storeyH / riserF));
    // A LANDING AT EACH END OF THE RUN, shared by the flights that meet there. See STAIR_LANDING_SLOTS.
    const slots = risers + 2 * STAIR_LANDING_SLOTS;
    const cores = b.cores;
    // THE FLIGHTS THIS NODE OWNS: one per shaft per storey it spans.
    let nF = 0;
    if (detail) {
        for (let s = f.storey; s < f.storey + f.span && s < b.storeys; s++) {
            const up = s % 2 === 0; // switchback: alternate direction each storey
            const baseY = b.seatY + Math.round(s * b.storeyH);
            for (let c = 0; c < cores.length; c++) {
                const run = alongSpan(cores[c], b.alongU), acr = acrossSpan(cores[c], b.alongU);
                const mid = (acr.lo + acr.hi) * 0.5 * iv;
                flBase[nF] = baseY;
                flUp[nF] = up ? 1 : 0;
                flRunLo[nF] = run.lo * iv;
                flTread[nF] = (run.hi - run.lo) * iv / slots;
                // ...and alternate the HALF of the shaft too. See STAIR_RISER's note: stacking flight N+1
                // over flight N leaves zero headroom at the top of N and seals the stairwell.
                flLandLo[nF] = up ? acr.lo * iv : mid;
                flLandHi[nF] = up ? mid : acr.hi * iv;
                flFullLo[nF] = acr.lo * iv;
                flFullHi[nF] = acr.hi * iv;
                nF++;
            }
        }
    }
    // The half of each shaft this storey's slab leaves OPEN, for the flight arriving from below.
    let nH = 0;
    if (slab) {
        const up = f.storey % 2 === 0;
        for (let c = 0; c < cores.length; c++) {
            const run = alongSpan(cores[c], b.alongU), acr = acrossSpan(cores[c], b.alongU);
            const mid = (acr.lo + acr.hi) * 0.5 * iv;
            holeRunLo[nH] = run.lo * iv;
            holeRunHi[nH] = run.hi * iv;
            holeAcrLo[nH] = up ? mid : acr.lo * iv;
            holeAcrHi[nH] = up ? acr.hi * iv : mid;
            nH++;
        }
    }
    // COLUMNS AND CEILING LIGHTS stand on the same lines the partitions above use, so the lobby's
    // colonnade lines up with the building rather than being a grid of its own.
    const colAlong = [];
    const colAcross = [];
    const litAcross = [];
    const scnAcross = [];
    if (lights) {
        for (const c of f.cuts)
            colAlong.push(c * iv);
        // A storey with no room zone still needs lighting; one bay at its centre is the whole of it.
        if (colAlong.length === 0)
            colAlong.push(0);
        const bnd = acrossSpan(f.band, b.alongU), acr = acrossSpan(f.plate, b.alongU);
        if (lobby) {
            const half = COLUMN_SIZE * 0.5 * iv;
            colAcross.push(bnd.lo * iv - half, bnd.hi * iv + half);
            litAcross.push(0, (acr.lo + bnd.lo) * 0.5 * iv, (acr.hi + bnd.hi) * 0.5 * iv);
            // ...and a sconce on each column's CORRIDOR-FACING face. Not its centre: a column is three
            // voxels across, so a light buried in the middle of one is walled in and lights nothing.
            scnAcross.push(bnd.lo * iv, bnd.hi * iv);
        }
        else {
            litAcross.push(0);
        }
    }
    const colT = Math.max(COLUMN_SIZE * iv, minThicknessFine(Math.abs(b.grid.cos) + Math.abs(b.grid.sin), scale));
    // The light is ONE VOXEL, and that is why it is snapped to the lattice rather than drawn as a small
    // box in the building's turned frame: a sub-voxel box at an arbitrary yaw catches nought, one or
    // four samples depending on where it lands, so it is either invisible or a blob. A unit box centred
    // exactly ON a sample catches that sample and no other, at every yaw.
    const litY = snapTo(ceilY - scale, scale);
    const bxF = b.x * iv, bzF = b.z * iv;
    const hv = scale * 0.5; // half a voxel of THIS level — a lamp is one of them
    const lastTop = b.seatY + b.tiers[b.tiers.length - 1].top;
    // SCONCES ON THE ARCADE'S PIERS, on the face that looks into the lobby, at the level the ground
    // floor would have ended at had the lobby not swallowed it. On the PIER lattice, so each one lands
    // on a pier rather than in the middle of a bay — and snapped to the voxel lattice for the same
    // reason the ceiling lights are (drawFloor, above).
    let nSconce = 0;
    const sconceY = Math.round(b.seatY + b.storeyH);
    if (lights && lobby) {
        const eU = f.plate.u1 * iv, eV = f.plate.v1 * iv;
        for (const [alongHalf, acrossEdge, onU] of [
            [f.tier.halfV, eU, true], [f.tier.halfU, eV, false],
        ]) {
            for (const pm of postPositions(alongHalf, b.windowPitch, LOBBY_POST_WIDTH)) {
                for (const side of [-1, 1]) {
                    if (nSconce >= MAX_SCONCES)
                        break;
                    const a = pm * iv, x = side * acrossEdge;
                    const su = onU ? x : a, sv = onU ? a : x;
                    sconceX[nSconce] = snapTo(bxF + su * b.grid.cos - sv * b.grid.sin, scale);
                    sconceZ[nSconce] = snapTo(bzF + su * b.grid.sin + sv * b.grid.cos, scale);
                    nSconce++;
                }
            }
        }
    }
    for (let k = kLo; k <= kHi; k++) {
        const worldZ = (z0 + k * scale) * VOXEL_SCALE;
        for (let i = iLo; i <= iHi; i++) {
            const worldX = (x0 + i * scale) * VOXEL_SCALE;
            const dx = worldX - b.x, dz = worldZ - b.z;
            const su = (dx * b.grid.cos + dz * b.grid.sin) * iv;
            const sv = (-dx * b.grid.sin + dz * b.grid.cos) * iv;
            const qPU = spanQ(su, pU0, pU1), qPV = spanQ(sv, pV0, pV1);
            if (qPU > band || qPV > band)
                continue;
            const sAlong = b.alongU ? su : sv;
            const sAcross = b.alongU ? sv : su;
            // The open half of each shaft, subtracted from the slab.
            let hole = -Infinity;
            for (let h = 0; h < nH; h++) {
                const d = boxDepth2(spanQ(sAlong, holeRunLo[h], holeRunHi[h]), spanQ(sAcross, holeAcrLo[h], holeAcrHi[h]));
                if (d > hole)
                    hole = d;
            }
            // The slot this column stands in, and its two neighbours: a flight is a union of tread boxes
            // and the riser between two of them is only measured if both are evaluated.
            for (let n = 0; n < nF; n++) {
                flQLand[n] = spanQ(sAcross, flLandLo[n], flLandHi[n]);
                const full = spanQ(sAcross, flFullLo[n], flFullHi[n]);
                flQFull[n] = full;
                if (full <= band)
                    flSlot[n] = Math.floor((sAlong - flRunLo[n]) / flTread[n]);
            }
            // The one column and the one light this sample can possibly be inside — one lookup over a
            // list of a handful, per COLUMN rather than per voxel, and BROAD-PHASED like everything else
            // here: a colonnade is a dozen posts over a whole plate and a light is one blob per bay, so
            // almost every column drops both before the row loop is entered at all.
            const bay = colAlong.length > 0 ? colAlong[nearest(colAlong, sAlong)] : 0;
            let colQA = Infinity, colQB = Infinity;
            if (lobby && colAcross.length > 0) {
                const qa = slabQ(sAlong, bay, colT * 0.5);
                const qb = slabQ(sAcross, colAcross[nearest(colAcross, sAcross)], colT * 0.5);
                if (qa <= band && qb <= band) {
                    colQA = qa;
                    colQB = qb;
                }
            }
            // A sconce, but only for the thin ring of columns that can reach the wall at all — a linear
            // scan over two dozen of them is cheap there and would not be over the whole plate.
            let scDX = Infinity, scDZ = Infinity;
            if (nSconce > 0 && (qPU > -2 || qPV > -2)) {
                const wx = x0 + i * scale, wz = z0 + k * scale;
                for (let n = 0; n < nSconce; n++) {
                    const dx = wx - sconceX[n], dz = wz - sconceZ[n];
                    if (dx > -2 * scale && dx < 2 * scale && dz > -2 * scale && dz < 2 * scale) {
                        scDX = dx;
                        scDZ = dz;
                        break;
                    }
                }
            }
            // The one lattice voxel this column could BE, for each kind of light hung on the bay lattice:
            // the ceiling above it, and the sconce on the colonnade beside it. `snap` leaves its answer in
            // snapDX/snapDZ so neither has to allocate a pair per column.
            const wxF = x0 + i * scale, wzF = z0 + k * scale;
            const snap = (lc) => {
                const lsu = b.alongU ? bay : lc, lsv = b.alongU ? lc : bay;
                snapDX = wxF - snapTo(bxF + lsu * b.grid.cos - lsv * b.grid.sin, scale);
                snapDZ = wzF - snapTo(bzF + lsu * b.grid.sin + lsv * b.grid.cos, scale);
                return snapDX > -2 * scale && snapDX < 2 * scale
                    && snapDZ > -2 * scale && snapDZ < 2 * scale;
            };
            let litDX = Infinity, litDZ = Infinity;
            if (lights && litAcross.length > 0 && snap(litAcross[nearest(litAcross, sAcross)])) {
                litDX = snapDX;
                litDZ = snapDZ;
            }
            let colLitDX = Infinity, colLitDZ = Infinity;
            if (lights && scnAcross.length > 0 && snap(scnAcross[nearest(scnAcross, sAcross)])) {
                colLitDX = snapDX;
                colLitDZ = snapDZ;
            }
            for (let j = jLo; j <= jHi; j++) {
                const fineY = y0 + j * scale;
                let depth = -Infinity;
                let material = b.body;
                // STOREY 0 HAS NO SLAB: its floor is the top of the plinth, which the shell has already drawn
                // solid across the whole base tier. Drawing one anyway would raise the ground floor half a
                // metre above the doorway the shell cut for it.
                if (slab) {
                    let d = boxDepth3(qPU, qPV, spanQ(fineY, slabBottom, f.slabY));
                    if (-hole < d)
                        d = -hole;
                    if (d > depth) {
                        depth = d;
                        // The slab's LOWEST row is the ceiling of the storey below it. A cap cannot do this — it
                        // is one voxel at every level — so the top storey keeps a roof-material ceiling.
                        material = fineY < slabBottom + scale ? CEILING_MATERIAL : b.floorMat;
                    }
                }
                for (let n = 0; n < nF; n++) {
                    if (flQFull[n] > band)
                        continue;
                    const up = flUp[n] === 1;
                    for (let e = -1; e <= 1; e++) {
                        const kk = flSlot[n] + e;
                        if (kk < 0 || kk >= slots)
                            continue;
                        const slot = up ? kk : slots - 1 - kk;
                        // Slots below the first landing sit at the flight's base, slots above the last one at its
                        // top, and the clamp does both: the rest climb between them.
                        const rise = Math.max(0, Math.min(risers, slot - STAIR_LANDING_SLOTS + 1));
                        const stepTop = flBase[n] + rise * riserF;
                        // A landing is the shaft's FULL width, at the slab's own thickness, so the two flights
                        // that meet there are joined by a platform you can stand on and turn on.
                        const foot = slot < STAIR_LANDING_SLOTS || slot >= slots - STAIR_LANDING_SLOTS;
                        if (!foot && flQLand[n] > band)
                            continue;
                        const d = boxDepth3(spanQ(sAlong, flRunLo[n] + kk * flTread[n], flRunLo[n] + (kk + 1) * flTread[n]), foot ? flQFull[n] : flQLand[n], foot ? spanQ(fineY, stepTop - slabT, stepTop)
                            : spanQ(fineY, stepTop - treadT, stepTop));
                        // A LANDING IS A FLOOR and reads the same way round as one: ceiling underneath, and on
                        // top the floor's material — or the ROOF's, for the landing the last flight arrives on,
                        // because that one is the roof. Only the treads are treads.
                        if (d > depth) {
                            depth = d;
                            material = !foot ? STAIR_MATERIAL
                                : fineY < stepTop - slabT + scale ? CEILING_MATERIAL
                                    : (stepTop >= lastTop - 0.5 ? b.roof : b.floorMat);
                        }
                    }
                }
                // THE COLONNADE. A double-height lobby with no partitions has nothing else holding the tower
                // up, and it should look like it.
                if (colQA < Infinity) {
                    const d = boxDepth3(colQA, colQB, spanQ(fineY, b.seatY, ceilY + slabT));
                    if (d > depth) {
                        depth = d;
                        material = b.body;
                    }
                }
                // A single lava voxel under the ceiling of every bay. Liquid, so it neither collides nor
                // blocks its own light; MATERIAL_EMISSION_LUT seeds the block-light BFS off it.
                if (litDX < Infinity) {
                    const d = boxDepth3(Math.abs(litDX) - hv, Math.abs(litDZ) - hv, Math.abs(fineY - litY) - hv);
                    if (d > depth) {
                        depth = d;
                        material = LIGHT_MATERIAL;
                    }
                }
                if (scDX < Infinity) {
                    const d = boxDepth3(Math.abs(scDX) - hv, Math.abs(scDZ) - hv, Math.abs(fineY - sconceY) - hv);
                    if (d > depth) {
                        depth = d;
                        material = LIGHT_MATERIAL;
                    }
                }
                if (colLitDX < Infinity) {
                    const d = boxDepth3(Math.abs(colLitDX) - hv, Math.abs(colLitDZ) - hv, Math.abs(fineY - sconceY) - hv);
                    if (d > depth) {
                        depth = d;
                        material = LIGHT_MATERIAL;
                    }
                }
                const w = coarseWeight(depth, scale);
                if (w <= -0.5)
                    continue;
                const index = i + j * CHUNK_SIZE + k * CHUNK_SIZE * CHUNK_SIZE;
                sink.data[index] = mergeSurfaceWord(sink.data[index], w, material);
            }
        }
    }
}
/** Per-node scratch for the flights and the shaft openings. Two shafts, at most two storeys. */
const MAX_FLIGHTS = MAX_CORES * LOBBY_STOREYS;
const flBase = new Float64Array(MAX_FLIGHTS);
const flUp = new Int32Array(MAX_FLIGHTS);
const flRunLo = new Float64Array(MAX_FLIGHTS);
const flTread = new Float64Array(MAX_FLIGHTS);
const flLandLo = new Float64Array(MAX_FLIGHTS);
const flLandHi = new Float64Array(MAX_FLIGHTS);
const flQLand = new Float64Array(MAX_FLIGHTS);
const flFullLo = new Float64Array(MAX_FLIGHTS);
const flFullHi = new Float64Array(MAX_FLIGHTS);
const flQFull = new Float64Array(MAX_FLIGHTS);
const flSlot = new Int32Array(MAX_FLIGHTS);
const holeRunLo = new Float64Array(MAX_CORES);
const holeRunHi = new Float64Array(MAX_CORES);
const holeAcrLo = new Float64Array(MAX_CORES);
const holeAcrHi = new Float64Array(MAX_CORES);
/** Four faces of piers on the widest tier a lobby ever has; the cap is a backstop, not a budget. */
const MAX_SCONCES = 64;
const sconceX = new Int32Array(MAX_SCONCES);
const sconceZ = new Int32Array(MAX_SCONCES);
/** Scratch for the lattice snap in drawFloor — one answer at a time, per column. */
let snapDX = 0, snapDZ = 0;
export function roomNode(parent, f, rect, k, side) {
    const b = f.building;
    const hu = (rect.u1 - rect.u0) * 0.5, hv = (rect.v1 - rect.v0) * 0.5;
    const cu = (rect.u0 + rect.u1) * 0.5, cv = (rect.v0 + rect.v1) * 0.5;
    const cx = (b.x + cu * b.grid.cos - cv * b.grid.sin) / VOXEL_SCALE;
    const cz = (b.z + cu * b.grid.sin + cv * b.grid.cos) / VOXEL_SCALE;
    const c = Math.abs(b.grid.cos), sn = Math.abs(b.grid.sin);
    const hx = (hu * c + hv * sn) / VOXEL_SCALE, hz = (hu * sn + hv * c) / VOXEL_SCALE;
    const id = hashInt2(parent.id, k * 2 + (side > 0 ? 1 : 0));
    return {
        kind: 'room',
        id,
        seed: hashInt2(id, ROOM_NODE_SALT),
        aabb: {
            minX: cx - hx - ROOM_BAND, maxX: cx + hx + ROOM_BAND + 1,
            minY: f.slabY, maxY: f.slabY + Math.ceil(b.storeyH) + 1,
            minZ: cz - hz - ROOM_BAND, maxZ: cz + hz + ROOM_BAND + 1,
        },
        depth: 4,
        data: { floor: f, rect, side, capHigh: k === f.rooms - 1 },
    };
}
export function makeRoomKind() {
    return {
        kind: 'room',
        maxLod: MAX_ROOM_LOD,
        expandAtLod: -1, // a leaf. Furniture and contents hang here next.
        children: () => [],
        ySpan: (node) => ({ bottom: node.aabb.minY, top: node.aabb.maxY - 1 }),
        emit(node, sink) {
            drawRoom(node.data, node.aabb, sink);
        },
    };
}
/**
 * A room's own walls: the partition on its LOW edge along the corridor, the wall facing the corridor
 * with a doorway in it, and its ceiling light.
 *
 * THE LOW SIDE OWNS THE SHARED WALL — the same rule SurfaceNets uses for the band between two chunks.
 * A partition between two rooms belongs to the one below it, so it is drawn exactly once; the LAST
 * slice also caps its high edge, because there is no room above it to do so. Without a rule like this
 * the same wall is written by both neighbours: harmless while the material matches, and a silent
 * double-cost that becomes a visible seam the day it does not.
 *
 * EVERY PARTITION SITS ON A WINDOW GAP, because the cuts it is built from are the gaps (BuildingPlan,
 * rule 3). That is what keeps a wall from arriving mid-window and leaving a slot of daylight down the
 * join — nothing here has to check for it.
 *
 * The corridor-facing wall is not shared with anything, so each room draws its own — and every one of
 * them carries a doorway, which is what makes `room -> corridor -> hall -> stairs` true on every
 * storey without a connectivity search. Nothing has to be subtracted for the shafts: the room zone
 * stops STAIR_CLEARANCE short of both, so no wall can reach one.
 */
function drawRoom(r, aabb, sink) {
    const f = r.floor;
    const b = f.building;
    const scale = 1 << sink.lod;
    const span = CHUNK_SIZE * scale;
    const x0 = sink.cx * span, y0 = sink.cy * span, z0 = sink.cz * span;
    const iLo = Math.max(0, Math.ceil((aabb.minX - x0) / scale));
    const iHi = Math.min(CHUNK_SIZE - 1, Math.floor((aabb.maxX - 1 - x0) / scale));
    const kLo = Math.max(0, Math.ceil((aabb.minZ - z0) / scale));
    const kHi = Math.min(CHUNK_SIZE - 1, Math.floor((aabb.maxZ - 1 - z0) / scale));
    const { jLo, jHi } = rowRange(aabb, scale, y0);
    if (iLo > iHi || kLo > kHi || jLo > jHi)
        return;
    const band = surfaceBandFine(scale);
    const iv = 1 / VOXEL_SCALE;
    // A 0.25 m partition is ONE fine voxel and cannot survive the lattice at any yaw: measured, its
    // thinnest 5% rendered at 0.13 m and 19 places in 11346 had no partition at all. Floored like the
    // shell, against the same normal.
    const th = Math.max(PARTITION_THICKNESS * iv, minThicknessFine(Math.abs(b.grid.cos) + Math.abs(b.grid.sin), scale));
    // `slabY` IS the finished floor at every storey — the slab hangs below it (drawFloor) — so a room
    // no longer has to special-case the ground floor, and its walls start where the terrace decks do.
    const slabT = Math.max(SLAB_THICKNESS / VOXEL_SCALE, scale);
    const floorTop = f.slabY;
    const ceilY = f.slabY + b.storeyH - slabT;
    const doorH = DOOR_HEIGHT / VOXEL_SCALE;
    const halfDoor = DOOR_WIDTH * 0.5 * iv;
    // The walls this room owns, as boxes in the building's own frame. The spine's axis is `alongU`, so
    // the partitions cut across it and the corridor-facing wall lies along it.
    const alng = alongSpan(r.rect, b.alongU), acr = acrossSpan(r.rect, b.alongU);
    // Partitions are CENTRED on their cut, so both neighbours meet the same window gap.
    const partLo = alng.lo * iv - th * 0.5, partHi = alng.lo * iv + th * 0.5;
    const capLo = alng.hi * iv - th * 0.5, capHi = alng.hi * iv + th * 0.5;
    const xLo = acr.lo * iv, xHi = acr.hi * iv;
    // ...and the corridor-facing wall, on whichever side of the strip the corridor is.
    const corrLo = r.side < 0 ? xHi - th : xLo, corrHi = r.side < 0 ? xHi : xLo + th;
    const doorC = (alng.lo + alng.hi) * 0.5 * iv;
    // The room's single ceiling voxel, snapped to the lattice — see drawFloor.
    const litA = doorC, litB = (acr.lo + acr.hi) * 0.5 * iv;
    const lsu = b.alongU ? litA : litB, lsv = b.alongU ? litB : litA;
    const hv = scale * 0.5;
    const litX = snapTo(b.x * iv + lsu * b.grid.cos - lsv * b.grid.sin, scale);
    const litZ = snapTo(b.z * iv + lsu * b.grid.sin + lsv * b.grid.cos, scale);
    const litY = snapTo(ceilY - scale, scale);
    for (let k = kLo; k <= kHi; k++) {
        const worldZ = (z0 + k * scale) * VOXEL_SCALE;
        for (let i = iLo; i <= iHi; i++) {
            const worldX = (x0 + i * scale) * VOXEL_SCALE;
            const dx = worldX - b.x, dz = worldZ - b.z;
            const su = (dx * b.grid.cos + dz * b.grid.sin) * iv;
            const sv = (-dx * b.grid.sin + dz * b.grid.cos) * iv;
            const along = b.alongU ? su : sv; // along the corridor
            const across = b.alongU ? sv : su; // toward it
            const qPart = spanQ(along, partLo, partHi), qPartX = spanQ(across, xLo, xHi);
            const qCap = r.capHigh ? spanQ(along, capLo, capHi) : Infinity;
            const qCorr = spanQ(across, corrLo, corrHi), qCorrA = spanQ(along, alng.lo * iv, alng.hi * iv);
            const dLitX = x0 + i * scale - litX, dLitZ = z0 + k * scale - litZ;
            const nearLit = dLitX > -2 * scale && dLitX < 2 * scale
                && dLitZ > -2 * scale && dLitZ < 2 * scale;
            if ((qPart > band || qPartX > band) && (qCap > band || qPartX > band)
                && (qCorr > band || qCorrA > band) && !nearLit)
                continue;
            const qDoor = spanQ(along, doorC - halfDoor, doorC + halfDoor);
            for (let j = jLo; j <= jHi; j++) {
                const fineY = y0 + j * scale;
                const qy = spanQ(fineY, floorTop, ceilY);
                let depth = boxDepth3(qPart, qPartX, qy);
                let material = b.interior;
                if (r.capHigh) {
                    const d = boxDepth3(qCap, qPartX, qy);
                    if (d > depth)
                        depth = d;
                }
                // The doorway, and only in the corridor-facing wall — never in a shared partition.
                let corr = boxDepth3(qCorr, qCorrA, qy);
                const door = boxDepth3(qCorr, qDoor, spanQ(fineY, floorTop, floorTop + doorH));
                if (-door < corr)
                    corr = -door;
                if (corr > depth)
                    depth = corr;
                if (nearLit) {
                    const lit = boxDepth3(Math.abs(dLitX) - hv, Math.abs(dLitZ) - hv, Math.abs(fineY - litY) - hv);
                    if (lit > depth) {
                        depth = lit;
                        material = LIGHT_MATERIAL;
                    }
                }
                const w = coarseWeight(depth, scale);
                if (w <= -0.5)
                    continue;
                const index = i + j * CHUNK_SIZE + k * CHUNK_SIZE * CHUNK_SIZE;
                sink.data[index] = mergeSurfaceWord(sink.data[index], w, material);
            }
        }
    }
}
//# sourceMappingURL=city.js.map